import React, { useEffect, useMemo, useState } from 'react';
import apiConfig from '../config/api';
import LocationMap from './LocationMap';
import './PlanDetailsDrawer.css';

function toGeometry(rawGeometry) {
  if (!rawGeometry) return null;
  if (typeof rawGeometry === 'string') {
    try {
      return JSON.parse(rawGeometry);
    } catch {
      return null;
    }
  }
  return rawGeometry;
}

function formatDate(value) {
  if (!value) return '-';
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('he-IL');
  }
  const s = String(value).trim();
  if (!s) return '-';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString('he-IL');
}

function getDomainName(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

function isDocsListingUrl(url) {
  const u = String(url || '').toLowerCase();
  return u.includes('gisn.tel-aviv.gov.il/tabaot/docs.aspx');
}

function getMapCoordinates(display, geometry) {
  const minX = Number(display?.bbox_min_x);
  const minY = Number(display?.bbox_min_y);
  const maxX = Number(display?.bbox_max_x);
  const maxY = Number(display?.bbox_max_y);
  if ([minX, minY, maxX, maxY].every(Number.isFinite)) {
    return { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
  }

  if (!geometry) return null;
  const points = Array.isArray(geometry?.rings)
    ? geometry.rings.flat()
    : Array.isArray(geometry?.paths)
      ? geometry.paths.flat()
      : geometry?.x !== undefined && geometry?.y !== undefined
        ? [[geometry.x, geometry.y]]
        : [];
  const good = points.filter((p) => Array.isArray(p) && Number.isFinite(Number(p[0])) && Number.isFinite(Number(p[1])));
  if (!good.length) return null;
  const sum = good.reduce((acc, p) => ({ x: acc.x + Number(p[0]), y: acc.y + Number(p[1]) }), { x: 0, y: 0 });
  return { x: sum.x / good.length, y: sum.y / good.length };
}

function getFallbackCoordinates(display) {
  const c = display?.fallbackCoordinates;
  if (!c || typeof c !== 'object') return null;
  if (Number.isFinite(Number(c.lat)) && Number.isFinite(Number(c.lng))) {
    return { lat: Number(c.lat), lng: Number(c.lng) };
  }
  if (Number.isFinite(Number(c.x)) && Number.isFinite(Number(c.y))) {
    return { x: Number(c.x), y: Number(c.y) };
  }
  return null;
}

function normalizeObjectivesText(value) {
  const text = String(value || '')
    .replace(/\s*\^\s*/g, '\n')
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  if (!text) return '';
  if (text.length === 250 && !/[.!?…]$/.test(text)) return `${text}…`;
  return text;
}

function isLikelyLowQualityObjectives(text) {
  const raw = String(text || '').trim();
  if (!raw) return true;
  const compact = raw.replace(/\s+/g, '');
  if (!compact) return true;
  const total = compact.length;
  const hebrew = (compact.match(/[\u0590-\u05FF]/g) || []).length;
  const latin = (compact.match(/[A-Za-z]/g) || []).length;
  const replacement = (compact.match(/�/g) || []).length;
  const symbols = (compact.match(/[^\u0590-\u05FFA-Za-z0-9.,:;!?'"()\-/%]/g) || []).length;
  const bidiMarks = (raw.match(/[\u200E\u200F\u202A-\u202E]/g) || []).length;
  const lines = raw.split('\n').map((line) => line.trim()).filter(Boolean);
  const shortLines = lines.filter((line) => line.length <= 3).length;
  const hebrewRatio = hebrew / total;
  const latinRatio = latin / total;
  const noiseRatio = (replacement + symbols) / total;
  const shortLineRatio = lines.length ? shortLines / lines.length : 0;
  return hebrewRatio < 0.25 || latinRatio > 0.2 || noiseRatio > 0.08 || bidiMarks > 5 || (shortLineRatio > 0.28 && lines.length > 10);
}

const PlanDetailsDrawer = ({ plan, onClose }) => {
  const [record, setRecord] = useState(null);
  const [recordKey, setRecordKey] = useState('');
  const [documents, setDocuments] = useState([]);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const isPermit = useMemo(() => {
    if (!plan) return false;
    return Boolean(
      plan?.isTelAvivPermit ||
      plan?.isComplotPermit ||
      plan?.isMunicipalPermit ||
      plan?.requestNumber ||
      plan?.permitNumber
    );
  }, [plan]);
  const shouldFetchTaRecord = useMemo(() => {
    if (!plan) return false;
    const source = String(plan?.dataSource || plan?.source || '').toLowerCase();
    return Boolean(
      plan?.isTelAvivMunicipal ||
      plan?.isTelAvivOutlinePlan ||
      source.includes('tel aviv municipality') ||
      source.includes('tel aviv db') ||
      source.includes('tel_aviv_municipal') ||
      source.includes('תל אביב-יפו')
    );
  }, [plan]);

  const candidates = useMemo(() => {
    if (!plan) return [];
    const base = isPermit
      ? [
          String(plan.requestNumber || '').trim(),
          String(plan.planNumber || '').trim(),
          String(plan.permitNumber || '').trim(),
          String(plan.id || '').trim(),
        ]
      : [
          String(plan.planNumber || '').trim(),
          String(plan.id || '').trim(),
        ];
    return base
      .map((v) => v.replace(/\s*\/\s*/g, '/').replace(/\s+/g, ' ').trim())
      .filter(Boolean)
      .filter((v, i, arr) => arr.indexOf(v) === i);
  }, [plan, isPermit]);

  const cacheKey = useMemo(() => {
    if (!plan) return '';
    const kind = isPermit ? 'permit' : 'plan';
    const layer = Number(plan?._taSourceLayer);
    const objectId = Number(plan?._taSourceObjectId);
    if (Number.isFinite(layer) && Number.isFinite(objectId)) {
      return `${kind}:${layer}:${objectId}`;
    }
    const primary = candidates[0] || String(plan?.id || '').trim();
    return primary ? `${kind}:${primary}` : '';
  }, [plan, isPermit, candidates]);

  useEffect(() => {
    let cancelled = false;
    const loadRecord = async () => {
      if (!plan || candidates.length === 0 || !shouldFetchTaRecord) return;
      setRecord(null);
      setRecordKey('');
      setDocuments([]);
      setLinks([]);
      setLoading(true);
      try {
        const kind = isPermit ? 'permit' : 'plan';
        const sourceLayer = Number(plan?._taSourceLayer);
        const sourceObjectId = Number(plan?._taSourceObjectId);
        if (Number.isFinite(sourceLayer) && Number.isFinite(sourceObjectId)) {
          const exactId = isPermit
            ? String(plan.requestNumber || plan.planNumber || plan.permitNumber || plan.id || sourceObjectId)
            : String(plan.planNumber || plan.id || sourceObjectId);
          const exactUrl = `${apiConfig.apiBaseUrl}/api/ta-records?kind=${kind}&id=${encodeURIComponent(
            exactId
          )}&sourceLayer=${encodeURIComponent(String(sourceLayer))}&sourceObjectId=${encodeURIComponent(
            String(sourceObjectId)
          )}`;
          const exactRes = await fetch(exactUrl);
          if (exactRes.ok) {
            const exactData = await exactRes.json();
            if (exactData?.record) {
              if (cancelled) return;
              setRecord(exactData.record);
              setRecordKey(cacheKey);
              setDocuments(Array.isArray(exactData.documents) ? exactData.documents : []);
              setLinks(Array.isArray(exactData.links) ? exactData.links : []);
              return;
            }
          }
        }

        for (const id of candidates) {
          const titleParam = encodeURIComponent(String(plan?.title || ''));
          const res = await fetch(
            `${apiConfig.apiBaseUrl}/api/ta-records?kind=${kind}&id=${encodeURIComponent(id)}&title=${titleParam}`
          );
          if (!res.ok) continue;
          const data = await res.json();
          if (data?.record) {
            if (cancelled) return;
            setRecord(data.record);
            setRecordKey(cacheKey);
            setDocuments(Array.isArray(data.documents) ? data.documents : []);
            setLinks(Array.isArray(data.links) ? data.links : []);
            break;
          }
        }
      } catch {
        // Keep fallback to passed-in plan only
      } finally {
        if (cancelled) return;
        setLoading(false);
      }
    };

    loadRecord();
    return () => {
      cancelled = true;
    };
  }, [plan, candidates, shouldFetchTaRecord, isPermit, cacheKey]);

  useEffect(() => {
    if (!plan) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [plan, onClose]);

  useEffect(() => {
    if (!plan) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [plan]);

  if (!plan) return null;

  const recordMatchesPlan = Boolean(record) && recordKey === cacheKey;
  const display = recordMatchesPlan ? record : plan;
  const awaitingResolvedRecord = shouldFetchTaRecord && loading && !recordMatchesPlan;
  const objectivesCandidate = normalizeObjectivesText(
    display?.mavat_objectives_full ||
    display?.mavatObjectivesFull ||
    display?.iplan_objectives ||
    display?.objectives ||
    ''
  );
  const objectivesText = isLikelyLowQualityObjectives(objectivesCandidate) ? '' : objectivesCandidate;
  const mapSource = awaitingResolvedRecord ? null : display;
  const geometry = toGeometry(mapSource?.geometry_json || mapSource?.geometryJson || null);
  const mapCoordinates = getMapCoordinates(mapSource, geometry) || getFallbackCoordinates(mapSource);
  const isFallbackMap = !getMapCoordinates(mapSource, geometry) && Boolean(mapCoordinates);
  const displayPlanUrl = String(display?.plan_url || display?.planUrl || '').trim();
  const externalLinks = (() => {
    const merged = [];
    if (displayPlanUrl && !isDocsListingUrl(displayPlanUrl)) {
      merged.push({
        label: isPermit ? 'Open permit page' : 'Open plan page',
        url: displayPlanUrl,
        status: 'available',
      });
    }
    for (const link of links || []) {
      const url = String(link?.resolved || link?.candidate || link?.url || '').trim();
      if (!url) continue;
      if (isDocsListingUrl(url)) continue;
      merged.push({
        label: String(link?.label || link?.source || 'Source link'),
        url,
        status: String(link?.status || ''),
      });
    }

    const seen = new Set();
    return merged.filter((item) => {
      if (!item.url) return false;
      const looksLikeMavat = String(item.url).includes('mavat.iplan.gov.il');
      if (!looksLikeMavat && item.status && String(item.status).toLowerCase() === 'unavailable') return false;
      if (seen.has(item.url)) return false;
      seen.add(item.url);
      return true;
    });
  })();
  const primaryPlanLink = (() => {
    const mavat = externalLinks.find((link) => String(link.url || '').includes('mavat.iplan.gov.il'));
    if (mavat) return mavat;
    return externalLinks[0] || null;
  })();
  const extraExternalLinks = externalLinks.filter((link) => link.url !== primaryPlanLink?.url);
  const docLinks = documents
    .map((doc, index) => ({
      label: `מסמך ${index + 1}`,
      url: doc?.doc_url,
    }))
    .filter((doc) => String(doc.url || '').trim())
    .slice(0, 6);
  const actionLinks = [
    ...(primaryPlanLink
      ? [{
          label: isPermit ? 'עמוד היתר' : 'עמוד תוכנית',
          url: primaryPlanLink.url,
        }]
      : []),
    ...extraExternalLinks.slice(0, 3).map((link) => ({
      label: String(link.label || 'קישור מקור'),
      url: link.url,
    })),
    ...docLinks,
  ];
  const rows = isPermit
    ? [
        ['מספר בקשה', display?.request_number || display?.requestNumber || display?.planNumber || '-'],
        ['מספר היתר', display?.permit_number || display?.permitNumber || '-'],
        ['סטטוס', display?.status || '-'],
        ['סוג בקשה', display?.request_type || display?.requestType || '-'],
        ['תאריך הגשה', formatDate(display?.submission_date || display?.submissionDate)],
        ['תאריך אישור', formatDate(display?.approval_date || display?.approvalDate)],
      ]
    : [
        ['מספר תוכנית', display?.plan_number || display?.planNumber || '-'],
        ['סטטוס', display?.status || '-'],
        ['סוג תוכנית', display?.plan_type || display?.planType || '-'],
        ['תאריך הפקדה', formatDate(display?.submission_date || display?.submissionDate)],
        ['תאריך אישור', formatDate(display?.approval_date || display?.approvalDate)],
      ];
  const isInitialDetailsLoading = awaitingResolvedRecord;
  const visibleRows = awaitingResolvedRecord
    ? []
    : loading
    ? rows.filter(([, value]) => value && value !== '-')
    : rows;

  const closeDrawer = () => onClose();

  return (
    <div className="plan-drawer-overlay" onClick={onClose}>
      <aside
        className="plan-drawer-panel"
        dir="rtl"
        role="dialog"
        aria-modal="true"
        aria-label="Plan details"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="plan-drawer-header">
          <button
            type="button"
            className="plan-drawer-close"
            onClick={closeDrawer}
            aria-label="Close details"
          >
            <span aria-hidden="true">×</span>
          </button>
          <div className="plan-drawer-title-wrap">
            <div className="plan-drawer-number">
              {isPermit
                ? display?.request_number || display?.requestNumber || display?.permit_number || display?.permitNumber || ''
                : display?.plan_number || display?.planNumber || ''}
            </div>
            <h3>{display?.title || (isPermit ? 'פרטי היתר' : 'פרטי תוכנית')}</h3>
          </div>
        </div>

        <div className="plan-drawer-content">
          <section className="plan-drawer-card">
            <h4>פרטים מהירים</h4>
            <div className="plan-drawer-grid">
              {visibleRows.map(([label, value]) => (
                <React.Fragment key={label}>
                  <div className="k">{label}</div>
                  <div className="v">{loading ? value : (value || '-')}</div>
                </React.Fragment>
              ))}
              {isInitialDetailsLoading && visibleRows.length === 0 ? (
                <>
                  <div className="k">סטטוס</div>
                  <div className="v">טוען נתונים מלאים...</div>
                </>
              ) : null}
            </div>
            {!awaitingResolvedRecord && actionLinks.length > 0 ? (
              <div className="plan-drawer-actions">
                {actionLinks.map((link) => (
                  <button
                    key={`${link.url}-${link.label}`}
                    className="btn btn-outline btn-sm plan-drawer-action-btn"
                    onClick={() => window.open(link.url, '_blank', 'noopener,noreferrer')}
                  >
                    {link.label}
                    <span>{getDomainName(link.url)}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </section>

          <section className="plan-drawer-card">
            <h4>מיקום תחולה</h4>
            {mapCoordinates ? (
              <>
                <LocationMap
                  coordinates={mapCoordinates}
                  address={display?.title || display?.plan_number || 'Plan'}
                  stacked={true}
                  showStreetView={false}
                  geometry={geometry}
                  showTitles={false}
                  flat={true}
                  zoomDelta={-1}
                />
                {isFallbackMap ? (
                  <div className="plan-drawer-muted" style={{ marginTop: '8px' }}>
                    מוצגת מפת מיקום הנכס בלבד. תחולת תוכנית מדויקת אינה זמינה במקור זה.
                  </div>
                ) : null}
                {loading ? (
                  <div className="plan-drawer-muted" style={{ marginTop: '8px' }}>
                    טוען פרטי מפה נוספים...
                  </div>
                ) : null}
              </>
            ) : loading ? (
              <div className="plan-drawer-muted">טוען נתוני מפה...</div>
            ) : (
              <div className="plan-drawer-muted">No map geometry available for this plan yet.</div>
            )}
          </section>

          {!isPermit && objectivesText ? (
            <section className="plan-drawer-card">
              <h4>מטרות</h4>
              <p>{objectivesText}</p>
            </section>
          ) : null}

        </div>

        <div className="plan-drawer-mobile-close-wrap">
          <button
            type="button"
            className="btn btn-primary plan-drawer-mobile-close"
            onClick={closeDrawer}
          >
            סגור
          </button>
        </div>
      </aside>
    </div>
  );
};

export default PlanDetailsDrawer;
